import {
  COMPANY_ADDRESS_FULL,
  COMPANY_LOCATION,
  COMPANY_NAME,
  COMPANY_REGION,
  COMPANY_SHORT,
  COMPANY_TAGLINE,
  FOCUS_AREAS,
  HERO_IMAGE_URL,
  MAP_URL,
  MISSION_PILLARS,
  PROFILE_FACTS,
  PUBLIC_INFO,
} from './constants';

function Logo() {
  return (
    <span
      aria-hidden="true"
      className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-cream/40 bg-gradient-to-br from-coral via-sunset to-coral text-[11px] font-black tracking-wider text-tealDeep shadow-lift"
    >
      PACH
    </span>
  );
}

function Header() {
  const navItems = [
    { label: 'Mission', href: '#mission' },
    { label: 'Focus', href: '#focus' },
    { label: 'Partners', href: '#partners' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-tealDeep/20 bg-tealDeep/95 text-cream backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4">
        <a href="#top" className="flex min-w-0 items-center gap-3" aria-label={`${COMPANY_SHORT} home`}>
          <Logo />
          <span className="hidden flex-col sm:flex">
            <span className="font-display text-base font-bold leading-none text-cream">{COMPANY_SHORT}</span>
            <span className="mt-1 text-[11px] uppercase tracking-[0.22em] text-cream/65">Community Health</span>
          </span>
        </a>
        <nav className="hidden items-center gap-7 text-sm font-semibold text-cream/75 md:flex" aria-label="Primary">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="transition hover:text-coral">
              {item.label}
            </a>
          ))}
        </nav>
        <a
          href="#contact"
          className="rounded-full bg-coral px-5 py-2 text-sm font-bold text-tealDeep transition hover:bg-sunset"
        >
          View Address
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-tealDeep via-teal to-tealDeep text-cream">
      <div className="grain absolute inset-0 opacity-40" />
      <div
        aria-hidden="true"
        className="absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full bg-coral/25 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-40 -left-24 h-[460px] w-[460px] rounded-full bg-sage/30 blur-3xl"
      />
      <div className="relative mx-auto max-w-7xl px-5 py-20 lg:py-28">
        <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-cream/25 bg-cream/5 px-3 py-1 text-xs font-bold uppercase tracking-[0.24em] text-cream/85">
              <span className="h-1.5 w-1.5 rounded-full bg-coral" />
              Public health profile
            </p>
            <h1 className="mt-6 font-display text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
              {COMPANY_TAGLINE}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-cream/80">
              A community-rooted profile for an Ohio organization that convenes partners, residents, and providers
              around the everyday work of community wellness.
            </p>
            <p className="mt-3 max-w-2xl text-base leading-7 text-cream/65">
              The page shares only public-safe information: organization name, service area, and the listed mailing
              address — nothing more.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a
                href="#mission"
                className="rounded-full bg-coral px-6 py-3 text-center font-bold text-tealDeep transition hover:bg-sunset"
              >
                Our Mission
              </a>
              <a
                href="#contact"
                className="rounded-full border border-cream/40 px-6 py-3 text-center font-bold text-cream transition hover:bg-cream hover:text-tealDeep"
              >
                Public Address
              </a>
            </div>
          </div>

          <aside className="relative overflow-hidden rounded-xl2 border border-cream/15 bg-cream/5 shadow-soft">
            <img
              src={HERO_IMAGE_URL}
              alt="Hands together representing community health partnership"
              className="h-[420px] w-full object-cover opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-tealDeep via-tealDeep/30 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-7">
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-coral">Listed address</p>
              <p className="mt-3 font-display text-2xl font-bold leading-snug text-cream">
                {COMPANY_ADDRESS_FULL}
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function FactsStrip() {
  return (
    <section aria-label="Profile facts" className="bg-cream">
      <div className="mx-auto max-w-7xl px-5 py-10">
        <div className="grid gap-6 rounded-xl2 border border-line bg-surface px-6 py-7 shadow-lift md:grid-cols-3">
          {PROFILE_FACTS.map((fact) => (
            <div key={fact.label} className="flex items-start gap-4">
              <span className="mt-1 h-10 w-1 rounded-full bg-coral" aria-hidden="true" />
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-teal">{fact.label}</p>
                <p className="mt-2 font-display text-xl font-bold text-ink">{fact.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionHeading({ eyebrow, title, children, align = 'left' }) {
  return (
    <div className={align === 'center' ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'}>
      <p className="text-[11px] font-black uppercase tracking-[0.22em] text-coral">{eyebrow}</p>
      <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-ink sm:text-4xl">{title}</h2>
      {children ? <p className="mt-4 leading-7 text-muted">{children}</p> : null}
    </div>
  );
}

function MissionSection() {
  return (
    <section id="mission" className="bg-sand/60">
      <div className="mx-auto max-w-7xl px-5 py-20">
        <SectionHeading eyebrow="Mission" title="Four practical pillars for community health" align="center">
          A restrained framing of the organization's public posture — partnership work, wellness literacy, partner
          resources, and a steady civic voice.
        </SectionHeading>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {MISSION_PILLARS.map((pillar) => (
            <article
              key={pillar.title}
              className="group flex flex-col gap-4 rounded-xl2 border border-line bg-surface p-6 transition hover:-translate-y-1 hover:shadow-soft"
            >
              <span className="inline-flex w-fit rounded-full bg-teal/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-teal">
                {pillar.tag}
              </span>
              <h3 className="font-display text-xl font-bold leading-snug text-ink">{pillar.title}</h3>
              <p className="text-sm leading-6 text-muted">{pillar.body}</p>
              <span className="mt-auto h-1 w-10 rounded-full bg-coral transition group-hover:w-16" aria-hidden="true" />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FocusSection() {
  return (
    <section id="focus" className="bg-surface">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:grid-cols-[0.42fr_0.58fr]">
        <SectionHeading eyebrow="Focus" title="What this public profile keeps in view">
          The page borrows visual cues from community-health organizations: warm tones, generous spacing, and quiet
          editorial structure rather than salesy marketing blocks.
        </SectionHeading>
        <ol className="relative space-y-8 border-l border-dashed border-line pl-8">
          {FOCUS_AREAS.map((item, index) => (
            <li key={item.title} className="relative">
              <span
                aria-hidden="true"
                className="absolute -left-[42px] grid h-8 w-8 place-items-center rounded-full bg-coral font-display text-sm font-bold text-tealDeep shadow-lift"
              >
                {String(index + 1).padStart(2, '0')}
              </span>
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-teal">{item.label}</p>
              <h3 className="mt-2 font-display text-xl font-bold text-ink">{item.title}</h3>
              <p className="mt-2 leading-7 text-muted">{item.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function PartnersBand() {
  return (
    <section id="partners" className="bg-tealDeep text-cream">
      <div className="mx-auto max-w-7xl px-5 py-16">
        <div className="grid gap-10 lg:grid-cols-[0.55fr_0.45fr] lg:items-center">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-coral">Partners & residents</p>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight sm:text-4xl">
              A directory page for verification, not a transactional portal
            </h2>
            <p className="mt-5 max-w-xl leading-7 text-cream/75">
              The site is a quiet, public-facing card. It confirms who the organization is and where it sits — useful
              for partners, providers, and residents checking the public record.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {PUBLIC_INFO.map((item) => (
              <div
                key={item.label}
                className="rounded-xl2 border border-cream/15 bg-cream/5 p-5 backdrop-blur"
              >
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-cream/55">{item.label}</p>
                <p className="mt-2 font-display text-base font-bold leading-snug text-cream">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  return (
    <section id="contact" className="bg-cream">
      <div className="mx-auto max-w-5xl px-5 py-20">
        <div className="overflow-hidden rounded-xl2 border border-line bg-surface shadow-soft">
          <div className="grid gap-0 md:grid-cols-[0.45fr_0.55fr]">
            <div className="relative bg-gradient-to-br from-teal to-tealDeep p-8 text-cream">
              <div className="grain absolute inset-0 opacity-30" />
              <div className="relative">
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-coral">Contact</p>
                <h2 className="mt-3 font-display text-3xl font-bold leading-tight">{COMPANY_LOCATION}</h2>
                <p className="mt-4 max-w-xs leading-7 text-cream/75">
                  Use the listed address below for public profile and directory reference. {COMPANY_REGION}.
                </p>
              </div>
            </div>
            <div className="flex flex-col justify-between gap-6 p-8">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-teal">Organization Address</p>
                <p className="mt-3 font-display text-2xl font-bold leading-snug text-ink">
                  {COMPANY_ADDRESS_FULL}
                </p>
              </div>
              <a
                href={MAP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-fit items-center gap-2 rounded-full bg-coral px-6 py-3 font-bold text-tealDeep transition hover:bg-sunset"
              >
                Open map
                <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-tealDeep text-cream/75">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-8 text-sm md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <Logo />
          <p className="font-bold text-cream">
            &copy; {new Date().getFullYear()} {COMPANY_NAME}
          </p>
        </div>
        <p>{COMPANY_LOCATION}</p>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div id="top" className="min-h-screen bg-cream text-ink">
      <a
        href="#mission"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-coral focus:px-3 focus:py-2 focus:text-tealDeep"
      >
        Skip to content
      </a>
      <Header />
      <main>
        <Hero />
        <FactsStrip />
        <MissionSection />
        <FocusSection />
        <PartnersBand />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
